import React from 'react';

import styled from '@emotion/styled';

import { MEDIA, TOKENS } from '@root/components/www/home/tokens';

import { darkFooterLinkStyles } from '@shared/content/dark_footer_link_styles';
import { PrivacyPreferencesFooterLink } from '@shared/content/privacy_preferences_link';
import { CustomLink } from '@shared/custom_link';

import { accountUrl, careersBaseUrl } from '@utils/environment_urls';

import facebook from '@images/home/icon-facebook.svg';
import instagram from '@images/home/icon-instagram.svg';
import linkedin from '@images/home/icon-linkedin.svg';
import xLogo from '@images/home/icon-x.svg';
import footerLogo from '@images/home/logo-clutter-footer.png';
import ssl from '@images/home/ssl-badge.svg';

const FooterShell = styled.footer`
  background: ${TOKENS.color.sectionBgDark};
  color: ${TOKENS.color.onDark};
`;

const FooterMain = styled.div`
  padding: ${TOKENS.space[48]} ${TOKENS.space[24]} 0;

  ${MEDIA.md} {
    padding-left: ${TOKENS.space[40]};
    padding-right: ${TOKENS.space[40]};
  }

  ${MEDIA.lg} {
    padding-top: ${TOKENS.space[64]};
    padding-left: ${TOKENS.space[48]};
    padding-right: ${TOKENS.space[48]};
  }
`;

const FooterInner = styled.div`
  max-width: ${TOKENS.sectionMaxWidth};
  margin: 0 auto;
`;

const FooterTop = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${TOKENS.space[48]};
  padding-bottom: ${TOKENS.space[48]};
  text-align: center;

  ${MEDIA.lg} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    text-align: left;
  }
`;

const BrandBlock = styled.div`
  display: flex;
  max-width: 283px;
  flex-direction: column;
  gap: ${TOKENS.space[24]};
`;

const BrandLogo = styled.img`
  height: 31px;
  width: 126px;
  margin: 0 auto;
  object-fit: contain;

  ${MEDIA.lg} {
    margin: 0;
  }
`;

const BrandTagline = styled.p`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.bodySmSize};
  line-height: ${TOKENS.type.bodySmLineHeight};
  color: ${TOKENS.color.onDarkMuted};
`;

const SocialRow = styled.div`
  display: flex;
  justify-content: center;
  gap: ${TOKENS.space[8]};

  ${MEDIA.lg} {
    justify-content: flex-start;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${TOKENS.radius.pill};
  background: ${TOKENS.color.neutral0_7};
  transition: background 0.3s ease;

  &:hover {
    background: ${TOKENS.color.neutral0_10};
  }

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
  gap: ${TOKENS.space[32]};

  ${MEDIA.lg} {
    width: auto;
    flex-wrap: nowrap;
    justify-content: flex-end;
  }
`;

const LinkColumn = styled.div`
  flex: 1 1 140px;
  min-width: 0;
  max-width: 180px;
  text-align: left;

  ${MEDIA.lg} {
    flex: 0 0 180px;
    width: 180px;
    max-width: none;
  }
`;

const LinkHeading = styled.h3`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-size: 16px;
  font-weight: 500;
  color: ${TOKENS.color.onDark};
`;

const LinkList = styled.ul`
  margin: ${TOKENS.space[16]} 0 0;
  padding: 0;
  list-style: none;
`;

const FooterLink = styled(CustomLink)`
  ${darkFooterLinkStyles}
`;

const ExternalFooterLink = styled.a`
  ${darkFooterLinkStyles}
`;

const FooterBottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${TOKENS.space[16]};
  padding: ${TOKENS.space[32]} 0;

  ${MEDIA.lg} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Copyright = styled.p`
  margin: 0;
  font-family: ${TOKENS.font.sans};
  font-size: ${TOKENS.type.bodySmSize};
  line-height: ${TOKENS.type.bodySmLineHeight};
  color: ${TOKENS.color.onDarkMuted};
  text-align: center;

  ${MEDIA.lg} {
    text-align: left;
  }
`;

const CheckoutFooter: React.FC = () => (
  <FooterShell aria-label="footer">
    <FooterMain>
      <FooterInner>
        <FooterTop>
          <BrandBlock>
            <div>
              <BrandLogo src={footerLogo} alt="Clutter" />
              <BrandTagline style={{ marginTop: TOKENS.space[24] }}>
                Full-service storage in secure warehouses backed by Iron
                Mountain.
              </BrandTagline>
            </div>
            <SocialRow>
              <SocialLink
                href="https://www.instagram.com/clutter"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <img src={instagram} alt="" />
              </SocialLink>
              <SocialLink
                href="https://www.facebook.com/clutterstorage"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <img src={facebook} alt="" />
              </SocialLink>
              <SocialLink
                href="https://www.linkedin.com/company/clutter"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <img src={linkedin} alt="" />
              </SocialLink>
              <SocialLink
                href="https://x.com/clutter"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X"
              >
                <img src={xLogo} alt="" />
              </SocialLink>
            </SocialRow>
          </BrandBlock>

          <FooterLinks>
            <LinkColumn>
              <LinkHeading>Company</LinkHeading>
              <LinkList>
                <li>
                  <FooterLink to="/company">About us</FooterLink>
                </li>
                <li>
                  <ExternalFooterLink
                    href={careersBaseUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Careers
                  </ExternalFooterLink>
                </li>
                <li>
                  <FooterLink to="/partnerships">Partnerships</FooterLink>
                </li>
              </LinkList>
            </LinkColumn>
            <LinkColumn>
              <LinkHeading>Support</LinkHeading>
              <LinkList>
                <li>
                  <FooterLink to="/help">Help center</FooterLink>
                </li>
                <li>
                  <ExternalFooterLink href={accountUrl('support')}>
                    Customer support
                  </ExternalFooterLink>
                </li>
                <li>
                  <PrivacyPreferencesFooterLink />
                </li>
                <li>
                  <FooterLink to="/legal/privacy_policy">Privacy</FooterLink>
                </li>
              </LinkList>
            </LinkColumn>
          </FooterLinks>
        </FooterTop>

        <FooterBottom>
          <Copyright>
            © {new Date().getFullYear()} Clutter, Inc. All rights reserved. DOT
            Number #2719785
          </Copyright>
          <img src={ssl} alt="SSL Secure" width={52} height={26} />
        </FooterBottom>
      </FooterInner>
    </FooterMain>
  </FooterShell>
);

export { CheckoutFooter };
